import {
  Checkbox,
  FormControlLabel,
  TextField,
  SxProps,
  Theme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { CardCvcElement, CardExpiryElement } from "@stripe/react-stripe-js";
import { CardNumberElement } from "@stripe/react-stripe-js";
import { Controller, FieldErrors, Control } from "react-hook-form";
import { StripeField } from "@/components/booking/payment/stripeField";
import { PaymentCardValidationInfo } from "@/hooks/payments/useCreditCardInputs";

export type FormInput = {
  zipCode: string;
  shouldSaveCard: boolean;
};

type AddCreditCardFormProps = {
  control: Control<FormInput>;
  errors: FieldErrors<FormInput>;
  paymentCardValidationInfo: PaymentCardValidationInfo;
  showPaymentCardEmptyFieldErrors: boolean;
  onElementChange: (
    name: string,
    error: string
  ) => (event: { complete: boolean; error: { message: string } }) => void;
  defaultSaveCardAfterPayment: boolean;
  shouldAskForSavingCard: boolean;
  sx?: SxProps<Theme>;
};

const AddCreditCardForm = ({
  control,
  errors,
  paymentCardValidationInfo,
  showPaymentCardEmptyFieldErrors,
  onElementChange,
  defaultSaveCardAfterPayment,
  shouldAskForSavingCard,
  sx,
}: AddCreditCardFormProps) => {
  const {
    cardNumberError,
    expiredError,
    cvcError,
    cardNumberComplete,
    expiredComplete,
    cvcComplete,
  } = paymentCardValidationInfo;

  return (
    <form>
      <Grid container spacing={2} sx={sx}>
        <Grid size={12}>
          <StripeField
            stripeElement={CardNumberElement}
            error={
              Boolean(cardNumberError) ||
              (!cardNumberComplete && showPaymentCardEmptyFieldErrors)
            }
            labelErrorMessage={cardNumberError}
            onChange={onElementChange("cardNumberComplete", "cardNumberError")}
          />
        </Grid>

        <Grid size={6}>
          <StripeField
            stripeElement={CardExpiryElement}
            error={
              Boolean(expiredError) ||
              (!expiredComplete && showPaymentCardEmptyFieldErrors)
            }
            labelErrorMessage={expiredError}
            onChange={onElementChange("expiredComplete", "expiredError")}
          />
        </Grid>

        <Grid size={6}>
          <StripeField
            stripeElement={CardCvcElement}
            error={
              Boolean(cvcError) ||
              (!cvcComplete && showPaymentCardEmptyFieldErrors)
            }
            labelErrorMessage={cvcError}
            onChange={onElementChange("cvcComplete", "cvcError")}
          />
        </Grid>

        <Grid size={12}>
          <Controller
            defaultValue=""
            name="zipCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                data-dd-privacy="mask"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                fullWidth
                type="text"
                placeholder="Billing zip code"
                error={!!errors.zipCode}
                helperText={
                  errors.zipCode ? (errors.zipCode.message as string) : ""
                }
              />
            )}
          />
        </Grid>
        {shouldAskForSavingCard && (
          <Grid size={12}>
            <Controller
              name="shouldSaveCard"
              control={control}
              defaultValue={defaultSaveCardAfterPayment ?? false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox sx={{ py: 0 }} {...field} checked={field.value} />
                  }
                  label="Save the card for future payment"
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default AddCreditCardForm;
